<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
// custom (CRM v2 · F4): ações terminais do deal CRM no botão Resolver (Seam B).
// custom (Modeloja · Lote 7): as mesmas ações, agora alcançáveis pela paleta do Ctrl+K.
import {
  CMD_CRM_MARCAR_GANHO,
  CMD_CRM_MARCAR_PERDA,
} from 'dashboard/composables/cw_custom/crmCommandEvents.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCrmConversationDeal } from 'dashboard/composables/cw_custom/useCrmConversationDeal.js';
import crmBoardApi from 'dashboard/api/cw_custom/crmBoard.js';
import CrmWinComprasForm from 'dashboard/routes/dashboard/cw_custom/CrmWinComprasForm.vue';
import CrmLoseReasonPopover from 'dashboard/routes/dashboard/cw_custom/CrmLoseReasonPopover.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

// custom (2026-06-24): os popovers de ganho/perda são TELEPORTADOS para o <body> e
// posicionados em `fixed` ancorados à barra de ações. Isso escapa do stacking context
// criado pelo `backdrop-blur` do ConversationHeader — que fazia o popover de "lead
// perdido" renderizar ATRÁS do conteúdo da conversa (bug reportado). resolveActionsRef
// ancora a posição; positionPopover recalcula no abrir e em resize/scroll.
const resolveActionsRef = ref(null);
const popoverStyle = ref({});

const positionPopover = () => {
  const el = resolveActionsRef.value;
  if (!el?.getBoundingClientRect) return;
  const r = el.getBoundingClientRect();
  popoverStyle.value = {
    position: 'fixed',
    top: `${Math.round(r.bottom + 6)}px`,
    right: `${Math.round(Math.max(8, window.innerWidth - r.right))}px`,
    zIndex: 9999,
  };
};

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

// ── custom (CRM v2 · F4): estado do deal CRM vinculado à conversa (Seam B). ──
// Lê o MESMO cache module-scoped que o bloco Kanban (ContactPanel). isCrmWin liga
// o comportamento de venda APENAS quando há deal CRM aberto. Conversas não-CRM
// (inbox interna id=6, deal já fechado, conversa pré-CRM) → isCrmLinked/isOpen=false
// → botão e dropdown permanecem idênticos ao core (zero regressão).
const { accountId } = useAccount();
const crmConversationId = computed(() => currentChat.value?.id);
const {
  deal: crmDeal,
  stages: crmStages,
  isCrmLinked,
  isOpen: isDealOpen,
  applyDeal,
  refresh: refreshCrmDeal,
} = useCrmConversationDeal(accountId, crmConversationId);

// custom (2026-06-24 · Change 3): "Concluir venda" is shown only when the deal's current
// stage is at Apresentação or later. `crmStages` contains the non-terminal stages of the
// funnel ordered by posicao asc — same data already loaded by the composable for the
// ContactPanel Kanban (zero extra fetch).
const currentStagePosicao = computed(() => {
  if (!crmDeal.value || !crmStages.value.length) return null;
  const s = crmStages.value.find(st => st.id === crmDeal.value.stage_id);
  return s?.posicao ?? null;
});

const apresentacaoPosicao = computed(() => {
  const s = crmStages.value.find(st => st.slug === 'apresentacao');
  return s?.posicao ?? null;
});

// True when there is an open CRM deal AND its stage posicao >= apresentacao posicao.
const isAtOrPastApresentacao = computed(() => {
  const cur = currentStagePosicao.value;
  const ap = apresentacaoPosicao.value;
  if (cur === null || ap === null) return false;
  return cur >= ap;
});

// custom (2026-06-24 · Change 3): "Concluir venda" requires:
//   1. Conversation is open.
//   2. There is a linked open CRM deal.
//   3. Deal stage is at or past Apresentação.
const isCrmWin = computed(
  () =>
    isOpen.value &&
    isCrmLinked.value &&
    isDealOpen.value &&
    isAtOrPastApresentacao.value
);

// custom (2026-06-24 follow-up): any open CRM deal at ANY stage — gates the dropdown
// (chevron + "Marcar como lead perdido"). Conversations with no open CRM deal render
// no open-state action group at all (no button, no chevron, no Resolver).
// This is the original isCrmWin from F4, before the apresentação gate was added.
const hasOpenCrmDeal = computed(
  () => isOpen.value && isCrmLinked.value && isDealOpen.value
);

// The primary "Concluir venda" button is shown only at Apresentação or later.
const showOpenStateActions = computed(() => isOpen.value && isCrmWin.value);

// custom (2026-08-07): QUEM REABRE UMA CONVERSA DE CLIENTE É O CLIENTE.
//
// O botão "Reabrir" some em toda conversa que tem card no CRM — ganha, perdida ou aberta.
// Decisão do Gustavo, e o motivo é de DINHEIRO antes de ser de processo: reabrir uma
// conversa fora da janela de 24h obriga a próxima mensagem a sair como template, cobrado
// pela Meta. Um clique num botão cinza no canto da tela não pode ser o gesto que gasta.
//
// E o processo: lead que virou oportunidade perdida saiu do funil. Falar com ele de novo
// não é follow-up — follow-up acontece DENTRO da venda. Voltar a procurá-lo é campanha, e
// campanha é outra coisa, com outra régua, que ainda vamos desenhar (a base de quem já
// comprou é o alvo natural dela).
//
// Nada se perde: `Message#reopen_conversation` reabre a conversa resolvida SOZINHA quando
// chega mensagem do cliente. O caminho que ele quer preservar continua inteiro; o que sai é
// só a porta que o vendedor abria por fora.
//
// Conversa SEM card (equipe interna, canal sem CRM) mantém o botão do upstream — lá reabrir
// não custa nada e nem passa pela Meta.
const podeReabrir = computed(() => !isCrmLinked.value);

// 'idle' | 'win' | 'lose' — qual fluxo terminal do deal está aberto.
const crmOutcomeMode = ref('idle');
const crmSubmitting = ref(false);
const crmOutcomeError = ref('');

const closeCrmOutcome = () => {
  crmOutcomeMode.value = 'idle';
  crmOutcomeError.value = '';
};

// Abre o popover de "Lead perdido" (botão vermelho dedicado).
const openLosePopover = () => {
  crmOutcomeError.value = '';
  crmOutcomeMode.value = 'lose';
};

async function submitCrmWin(compras) {
  if (crmSubmitting.value || !crmDeal.value) return;
  const dealId = crmDeal.value.id;
  crmSubmitting.value = true;
  crmOutcomeError.value = '';
  try {
    const res = await crmBoardApi.winDeal(accountId.value, dealId, compras);
    applyDeal(res.data?.deal ?? res.data ?? null);
    crmOutcomeMode.value = 'idle';
    // O backend encerra a conversa (resolved) e o websocket atualiza o status;
    // refresh garante o estado do deal caso o socket atrase.
    refreshCrmDeal();
    useAlert('Venda concluída.');
  } catch (e) {
    crmOutcomeError.value =
      e.response?.data?.error ?? 'Erro ao concluir a venda. Tente novamente.';
  } finally {
    crmSubmitting.value = false;
  }
}

async function submitCrmLose(motivoPerda, motivoPerdaDetalhe = null) {
  if (crmSubmitting.value || !crmDeal.value) return;
  const dealId = crmDeal.value.id;
  crmSubmitting.value = true;
  crmOutcomeError.value = '';
  try {
    const res = await crmBoardApi.loseDeal(
      accountId.value,
      dealId,
      motivoPerda,
      motivoPerdaDetalhe
    );
    applyDeal(res.data?.deal ?? res.data ?? null);
    crmOutcomeMode.value = 'idle';
    refreshCrmDeal();
    useAlert('Lead marcado como perdido.');
  } catch (e) {
    crmOutcomeError.value =
      e.response?.data?.error ??
      'Erro ao marcar como perdido. Tente novamente.';
  } finally {
    crmSubmitting.value = false;
  }
}

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

// custom (CRM v2 · F4): botão primário — deal CRM aberto abre o fluxo de ganho;
// senão, resolve nativo. Definido após onCmdResolveConversation (que ele chama).
const onPrimaryResolve = () => {
  if (isCrmWin.value) {
    crmOutcomeError.value = '';
    crmOutcomeMode.value = 'win';
    return;
  }
  onCmdResolveConversation();
};

// custom (Modeloja · Lote 7): as ações terminais do card também pela paleta (Ctrl+K).
//
// Reaproveitam os MESMOS caminhos do botão — `onPrimaryResolve` e `openLosePopover` — em
// vez de fechar o negócio por conta própria. Um segundo caminho para marcar venda seria um
// segundo lugar para a regra divergir (checagem de compras, motivo de perda, o que a Meta
// cobra), e este projeto já pagou caro por ter dois leitores da mesma coisa.
//
// Conversa sem card CRM: `onPrimaryResolve` cai no resolve nativo do Chatwoot, que é o
// comportamento correto — e a perda simplesmente não tem o que abrir.
const onCmdCrmGanho = () => {
  onPrimaryResolve();
};
const onCmdCrmPerda = () => {
  if (isCrmLinked.value) openLosePopover();
};

const keyboardEvents = {
  // custom (2026-06-24): Alt+M (TOGGLE_SNOOZE_DROPDOWN, anunciado no modal de atalhos)
  // antes clicava a setinha do dropdown — removida nesta mudança. Reaproveitamos o atalho
  // para abrir direto o seletor de adiamento (ninja-keys), preservando a intenção (snooze).
  'Alt+KeyM': {
    action: () =>
      document.querySelector('ninja-keys')?.open({
        parent: 'snooze_conversation',
      }),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    // custom (CRM v2 · F4): roteia pelo botão primário — consistência atalho↔botão.
    // Deal CRM aberto → abre o fluxo de ganho (não resolve direto). Senão, resolve nativo.
    action: async () => {
      onPrimaryResolve();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      event.preventDefault();
      // custom (CRM v2 · F4): se for CRM-win, só abre o form de ganho — NÃO avança
      // para a próxima conversa (a conversa ainda não foi resolvida; avançar seria drift).
      if (isCrmWin.value) {
        onPrimaryResolve();
        return;
      }

      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
// custom (Modeloja · Lote 7)
useEmitter(CMD_CRM_MARCAR_GANHO, onCmdCrmGanho);
useEmitter(CMD_CRM_MARCAR_PERDA, onCmdCrmPerda);

// custom (2026-06-24): enquanto um popover (ganho/perda) está aberto, mantém a posição
// `fixed` em sincronia com a barra de ações (resize/scroll) e limpa os listeners ao fechar.
watch(crmOutcomeMode, mode => {
  if (mode === 'win' || mode === 'lose') {
    nextTick(positionPopover);
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
  } else {
    window.removeEventListener('resize', positionPopover);
    window.removeEventListener('scroll', positionPopover, true);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', positionPopover);
  window.removeEventListener('scroll', positionPopover, true);
});
</script>

<template>
  <div
    ref="resolveActionsRef"
    class="flex relative justify-end items-center resolve-actions"
  >
    <!--
      custom (2026-06-24): barra de ações da conversa CRM.
        • Conversa SEM deal CRM aberto → nada aqui (nem "Resolver"); só Reabrir/Abrir.
        • Deal CRM aberto ANTES da Apresentação → só o botão vermelho "Lead perdido".
        • Deal CRM aberto na Apresentação ou depois → "Concluir venda" (verde pastel) + "Lead perdido".
        • Resolvida → Reabrir; Pendente/Adiada → Abrir.
      Snooze / Marcar pendente migraram para o menu "⋮" (MoreActions). Os popovers de
      ganho/perda são teleportados para o <body> e posicionados em `fixed` (escape do
      stacking context do backdrop-blur do ConversationHeader — que escondia o popover
      de "lead perdido" atrás do conteúdo da conversa).
    -->
    <div v-if="hasOpenCrmDeal" class="flex items-center gap-2 flex-shrink-0">
      <!-- "Concluir venda" (verde pastel): só na Apresentação ou depois. -->
      <Button
        v-if="showOpenStateActions"
        label="Concluir venda"
        size="sm"
        color="green"
        no-animation
        :is-loading="isLoading"
        @click="onPrimaryResolve"
      />
      <!-- "Lead perdido" (vermelho): em qualquer etapa de um deal aberto. -->
      <Button
        label="Lead perdido"
        size="sm"
        color="ruby"
        variant="faded"
        icon="i-lucide-circle-x"
        no-animation
        :disabled="isLoading"
        @click="openLosePopover"
      />
    </div>

    <!-- Estados não-CRM: Reabrir (resolvida) / Abrir (pendente/adiada). -->
    <!-- custom (2026-08-07): "Reabrir" SOME em conversa de card do CRM (ver podeReabrir). -->
    <ButtonGroup
      v-else-if="(isResolved && podeReabrir) || showOpenButton"
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isResolved && podeReabrir"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="!outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
    </ButtonGroup>

    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />

    <!-- custom (2026-06-24): popovers terminais TELEPORTADOS para o <body>, posicionados
         em `fixed` (popoverStyle) ancorados à barra de ações. Escapam do stacking context
         do ConversationHeader (backdrop-blur) que escondia o popover atrás da conversa.
         crm-light-scope força os tokens light dentro do popover (tema global pode ser dark). -->
    <Teleport to="body">
      <!-- Form de ganho — reusa CrmWinComprasForm verbatim do board. -->
      <div
        v-if="crmOutcomeMode === 'win'"
        v-on-clickaway="closeCrmOutcome"
        :style="popoverStyle"
        class="crm-light-scope w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-n-weak bg-n-background shadow-[0_8px_24px_rgba(0,0,0,0.12)] overflow-hidden"
        role="dialog"
      >
        <CrmWinComprasForm
          :submitting="crmSubmitting"
          :error="crmOutcomeError"
          @confirm="submitCrmWin"
          @cancel="closeCrmOutcome"
        />
      </div>

      <!-- Popover de motivo de perda — reusa CrmLoseReasonPopover verbatim. O componente é
           nativamente `absolute bottom-full right-0`; aqui forçamos `static` para que flua
           dentro do wrapper `fixed` (que faz o posicionamento), abrindo para baixo. -->
      <div
        v-if="crmOutcomeMode === 'lose'"
        v-on-clickaway="closeCrmOutcome"
        :style="popoverStyle"
        class="crm-light-scope"
      >
        <CrmLoseReasonPopover
          class="!static !mt-0 !mb-0"
          :submitting="crmSubmitting"
          :error="crmOutcomeError"
          @confirm="submitCrmLose"
          @cancel="closeCrmOutcome"
        />
      </div>
    </Teleport>
  </div>
</template>
